import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Users, Copy } from "lucide-react";

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("verifying"); // verifying | success | failed
  const [filmId, setFilmId] = useState(null);
  // Watch-party success path — carries the share token so the host can
  // copy the link and start inviting immediately without a second click.
  const [partyToken, setPartyToken] = useState(null);
  const [partyExpiry, setPartyExpiry] = useState(null);

  useEffect(() => {
    // Flutterwave uses ``tx_ref``; Paystack uses ``reference``/``trxref``.
    // Both routes hit the same ``/payments/verify/{reference}`` — the
    // backend routes by the purchase row's stored currency, so any of
    // these param names resolves cleanly.
    const ref = params.get("tx_ref") || params.get("reference") || params.get("trxref");
    if (!ref) {
      setState("failed");
      return;
    }

    // Watch-party payments carry ``?party=1`` on the callback URL AND use
    // the ``cvwp_`` reference prefix. Verify against the party endpoint.
    const isParty = params.get("party") === "1" || ref.startsWith("cvwp_");
    if (isParty) {
      api.get(`/watch-party/verify/${ref}`).then((r) => {
        if (r.data.status === "active" || r.data.status === "scheduled") {
          setPartyToken(r.data.share_token);
          setPartyExpiry(r.data.expires_at);
          setFilmId(r.data.party?.film_id || null);
          setState("success");
        } else {
          setState("failed");
        }
      }).catch(() => setState("failed"));
      return;
    }

    api.get(`/payments/verify/${ref}`).then((r) => {
      if (r.data.status === "success") {
        setFilmId(r.data.film_id);
        setState("success");
      } else {
        setState("failed");
      }
    }).catch(() => setState("failed"));
  }, [params]);

  const shareUrl = partyToken ? `${window.location.origin}/watch-party/${partyToken}` : "";

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />
      <div className="pt-32 px-6 max-w-xl mx-auto" data-testid="payment-callback">
        <div className="rounded-2xl bg-surface border border-white/10 p-10 text-center">
          {state === "verifying" && (
            <>
              <Loader2 className="w-10 h-10 mx-auto text-terracotta animate-spin mb-4" />
              <h1 className="font-display text-2xl font-bold mb-2">Verifying your payment…</h1>
              <p className="text-foreground/60 text-sm">Just a moment.</p>
            </>
          )}
          {state === "success" && !partyToken && (
            <>
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-4" />
              <h1 className="font-display text-3xl font-black mb-2">Payment successful</h1>
              <p className="text-foreground/70 mb-6">You now own this film — rewatch any time, forever.</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => navigate(`/watch/${filmId}`)} className="bg-terracotta hover:bg-ember text-white" data-testid="callback-watch-btn">Watch now</Button>
                <Button onClick={() => navigate("/library")} variant="outline" className="border-white/10">Go to library</Button>
              </div>
            </>
          )}
          {state === "success" && partyToken && (
            <>
              <Users className="w-12 h-12 mx-auto text-terracotta mb-4" />
              <h1 className="font-display text-3xl font-black mb-2">Watch Party is live</h1>
              <p className="text-foreground/70 mb-4">Share this link — anyone can join, no account needed. Expires in 24 hours.</p>
              <div className="rounded-xl border border-terracotta/30 bg-terracotta/5 p-3 flex items-center gap-2 text-left mb-6" data-testid="callback-share-block">
                <code className="flex-1 min-w-0 text-xs text-foreground/90 font-mono truncate">{shareUrl}</code>
                <Button
                  size="sm"
                  className="h-8 bg-terracotta hover:bg-ember text-white shrink-0"
                  onClick={async () => {
                    try { await navigator.clipboard.writeText(shareUrl); } catch { /* silent */ }
                  }}
                  data-testid="callback-share-copy"
                >
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="flex gap-3 justify-center flex-wrap">
                <Button
                  onClick={() => window.open(shareUrl, "_blank")}
                  className="bg-terracotta hover:bg-ember text-white"
                  data-testid="callback-open-party-btn"
                >
                  Open party
                </Button>
                <Button
                  onClick={() => navigate(filmId ? `/film/${filmId}` : "/browse")}
                  variant="outline"
                  className="border-white/10"
                >
                  Back to film
                </Button>
              </div>
              {partyExpiry && (
                <p className="text-xs text-foreground/45 mt-4">
                  Expires {new Date(partyExpiry).toLocaleString()}
                </p>
              )}
            </>
          )}
          {state === "failed" && (
            <>
              <XCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
              <h1 className="font-display text-3xl font-black mb-2">Payment failed</h1>
              <p className="text-foreground/70 mb-6">We couldn't verify your transaction. Please try again.</p>
              <Button onClick={() => navigate("/browse")} className="bg-terracotta hover:bg-ember text-white">Back to browse</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
